import { Fragment, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Bar, Pie } from 'react-chartjs-2';
import {
  ArcElement, BarElement, CategoryScale, Chart, LinearScale, Tooltip, Legend,
} from 'chart.js';
import { reports, studies, type StudyReport,
         type ReportStudyTypeBucket, type ReportStudyRef } from '@/api/endpoints';
import type { StudyType } from '@/types';
import { Icon } from '@/components/Icon';
import { SearchableSelect } from '@/components/SearchableSelect';

Chart.register(ArcElement, BarElement, CategoryScale, LinearScale, Tooltip, Legend);

const fmtIsoDate = (d: Date): string => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};

const fmtDate = (iso: string): string => {
  const d = new Date(iso + 'T00:00:00');
  if (Number.isNaN(d.getTime())) return iso;
  return new Intl.DateTimeFormat('pl-PL', { dateStyle: 'medium' }).format(d);
};

interface Preset {
  id: string;
  label: string;
  range: () => { from: string; to: string };
}

const presets: Preset[] = [
  {
    id: 'this-month', label: 'Ten miesiąc',
    range: () => {
      const now = new Date();
      const from = new Date(now.getFullYear(), now.getMonth(), 1);
      const to = new Date(now.getFullYear(), now.getMonth() + 1, 0);
      return { from: fmtIsoDate(from), to: fmtIsoDate(to) };
    },
  },
  {
    id: 'last-month', label: 'Poprzedni miesiąc',
    range: () => {
      const now = new Date();
      const from = new Date(now.getFullYear(), now.getMonth() - 1, 1);
      const to = new Date(now.getFullYear(), now.getMonth(), 0);
      return { from: fmtIsoDate(from), to: fmtIsoDate(to) };
    },
  },
  {
    id: 'this-quarter', label: 'Ten kwartał',
    range: () => {
      const now = new Date();
      const q = Math.floor(now.getMonth() / 3);
      const from = new Date(now.getFullYear(), q * 3, 1);
      const to = new Date(now.getFullYear(), q * 3 + 3, 0);
      return { from: fmtIsoDate(from), to: fmtIsoDate(to) };
    },
  },
  {
    id: 'last-quarter', label: 'Poprzedni kwartał',
    range: () => {
      const now = new Date();
      const q = Math.floor(now.getMonth() / 3) - 1;
      const yearOffset = q < 0 ? -1 : 0;
      const qIdx = q < 0 ? q + 4 : q;
      const from = new Date(now.getFullYear() + yearOffset, qIdx * 3, 1);
      const to = new Date(now.getFullYear() + yearOffset, qIdx * 3 + 3, 0);
      return { from: fmtIsoDate(from), to: fmtIsoDate(to) };
    },
  },
  {
    id: 'this-year', label: 'Ten rok',
    range: () => {
      const y = new Date().getFullYear();
      return { from: `${y}-01-01`, to: `${y}-12-31` };
    },
  },
  {
    id: 'last-year', label: 'Poprzedni rok',
    range: () => {
      const y = new Date().getFullYear() - 1;
      return { from: `${y}-01-01`, to: `${y}-12-31` };
    },
  },
];

const MIESIACE_KROTKIE = ['sty', 'lut', 'mar', 'kwi', 'maj', 'cze', 'lip', 'sie', 'wrz', 'paź', 'lis', 'gru'];

function fmtMonthLabel(ym: string): string {
  const [year, month] = ym.split('-');
  const m = parseInt(month, 10);
  if (Number.isNaN(m) || m < 1 || m > 12) return ym;
  return `${MIESIACE_KROTKIE[m - 1]} ${year.slice(2)}`;
}

/**
 * Stała paleta kolorów per StudyType — deterministyczna kolejność dopasowana do enum-u w backendzie.
 * Każdy typ ma swoją barwę zarówno w pie jak i w stacked-bar (spójność wizualna).
 * Kolory dobrane tak, żeby były odróżnialne (różne odcienie, perceptualnie zbliżone luminancje).
 */
const TYPE_COLORS: Record<string, string> = {
  PRG_1A:        '#0891b2', // cyan
  PRG_1B:        '#06b6d4',
  PRG_1C:        '#22d3ee',
  KIP_2:         '#16a34a', // green
  OP_WOD_3A:     '#d97706', // amber
  OP_WOD_3B:     '#ea580c',
  OP_WOD_3C:     '#f97316',
  DOK_HYDRO_4A:  '#9333ea', // purple
  DOK_HYDRO_4B:  '#a855f7',
  DOK_HYDRO_4C:  '#c084fc',
  DOK_LIKW_5:    '#dc2626', // red
  ANALIZA_RYZYKA_6: '#7c5cff',  // ai
  PLAN_BEZP_7:   '#0d9488', // teal
  PLAN_RUCHU_8:  '#94a3b8', // ink
};

function colorForType(type: string): string {
  return TYPE_COLORS[type] ?? '#94a3b8';
}

export function RaportyOpracowania() {
  const navigate = useNavigate();
  const [from, setFrom] = useState<string>(() => presets[0].range().from);
  const [to, setTo] = useState<string>(() => presets[0].range().to);
  const [activePreset, setActivePreset] = useState<string | null>('this-month');
  const [studyType, setStudyType] = useState<string>('');
  const [studyTypes, setStudyTypes] = useState<StudyType[]>([]);
  const [data, setData] = useState<StudyReport | null>(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [expanded, setExpanded] = useState<Set<string>>(new Set());

  const toggleExpanded = (type: string) => {
    setExpanded(prev => {
      const next = new Set(prev);
      if (next.has(type)) next.delete(type);
      else next.add(type);
      return next;
    });
  };

  const load = (f: string, t: string, st: string) => {
    setLoading(true);
    setErr(null);
    reports.studies(f, t, st || undefined)
      .then(setData)
      .catch(e => setErr(e?.message ?? String(e)))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    load(from, to, studyType);
    studies.types().then(setStudyTypes).catch(() => { /* nonfatal */ });
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, []);

  const applyPreset = (p: Preset) => {
    const r = p.range();
    setFrom(r.from);
    setTo(r.to);
    setActivePreset(p.id);
    load(r.from, r.to, studyType);
  };

  const onCustomChange = (which: 'from' | 'to', value: string) => {
    setActivePreset(null);
    if (which === 'from') setFrom(value);
    else setTo(value);
  };

  const onStudyTypeChange = (value: string) => {
    setStudyType(value);
    load(from, to, value);
  };

  const generate = () => {
    if (!from || !to) return;
    load(from, to, studyType);
  };

  const selectedStudyTypeLabel = useMemo(() => {
    if (!studyType) return null;
    const t = studyTypes.find(s => s.id === studyType);
    return t ? `${t.abbreviation}-${t.code} — ${t.name}` : studyType;
  }, [studyType, studyTypes]);

  // Top 3 typy do mini-KPI nad wykresem — w prosty sposób pokazują najczęstsze
  const topTypes = useMemo(() => (data?.byType ?? []).slice(0, 3), [data]);

  return (
    <div className="max-w-[1400px]">
      <header className="flex items-end justify-between mb-5">
        <div>
          <h1>Raport: wykonane opracowania</h1>
          <div className="text-ink-400 text-[13px]">
            Liczba opracowań w wybranym zakresie dat (po dacie wysyłki do urzędu) z rozkładem po typach
            i miesięcznym szeregiem czasowym.
          </div>
        </div>
      </header>

      <div className="card mb-4">
        <div className="card-head">
          <h3 className="text-[13px] font-semibold text-ink-700">Zakres dat (po dacie wysyłki opracowania do urzędu)</h3>
        </div>
        <div className="card-body space-y-3">
          <div className="flex flex-wrap gap-2">
            {presets.map(p => (
              <button
                key={p.id}
                onClick={() => applyPreset(p)}
                className={'btn btn-sm ' + (activePreset === p.id ? 'btn-primary' : 'btn-secondary')}
              >
                {p.label}
              </button>
            ))}
          </div>

          <div className="flex flex-wrap items-end gap-3 pt-2 border-t border-ink-100">
            <div>
              <label className="block text-[11px] text-ink-500 mb-1">Od</label>
              <input type="date" value={from} max={to || undefined}
                     onChange={e => onCustomChange('from', e.target.value)}
                     className="input input-sm w-44"/>
            </div>
            <div>
              <label className="block text-[11px] text-ink-500 mb-1">Do</label>
              <input type="date" value={to} min={from || undefined}
                     onChange={e => onCustomChange('to', e.target.value)}
                     className="input input-sm w-44"/>
            </div>
            <div>
              <label className="block text-[11px] text-ink-500 mb-1">Typ opracowania</label>
              <SearchableSelect value={studyType} onChange={onStudyTypeChange}
                      className="select-sm min-w-[280px]">
                <option value="">Wszystkie typy</option>
                {studyTypes.map(t => (
                  <option key={t.id} value={t.id}>{t.abbreviation}-{t.code} — {t.name}</option>
                ))}
              </SearchableSelect>
            </div>
            <button onClick={generate} className="btn btn-primary btn-sm" disabled={loading || !from || !to}>
              <Icon name="check" className="w-3.5 h-3.5"/> Generuj raport
            </button>
            {data && !loading && (
              <div className="text-[12px] text-ink-500 ml-auto">
                Zakres: <span className="font-semibold text-ink-700">{fmtDate(data.from)}</span>
                {' – '}
                <span className="font-semibold text-ink-700">{fmtDate(data.to)}</span>
                {selectedStudyTypeLabel && (
                  <>
                    <br/>
                    <span className="text-ink-400">Typ:</span>{' '}
                    <span className="font-semibold text-ink-700">{selectedStudyTypeLabel}</span>
                  </>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {err && <div className="text-red-500 p-3 mb-3">Błąd: {err}</div>}
      {loading && <div className="text-ink-400 p-3 mb-3">Generowanie raportu…</div>}

      {data && !loading && (
        <>
          <div className="card mb-4">
            <div className="card-body">
              <div className="grid grid-cols-1 sm:grid-cols-4 gap-4">
                <SummaryTile label="Łącznie wykonanych" value={String(data.totalCount)} hint="W wybranym zakresie dat"/>
                <SummaryTile
                  label="Liczba unikalnych typów"
                  value={String(data.byType.filter(b => b.count > 0).length)}
                  hint={data.byType.length > 0 ? `z ${data.byType.length} dostępnych` : undefined}
                />
                {topTypes.slice(0, 2).map(t => (
                  <SummaryTile
                    key={t.type}
                    label={`Top: ${t.abbreviation}`}
                    value={String(t.count)}
                    hint={t.displayName}
                  />
                ))}
                {topTypes.length < 2 && Array.from({ length: 2 - topTypes.length }).map((_, i) => (
                  <SummaryTile key={'empty-' + i} label="—" value="—"/>
                ))}
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-4">
            <div className="card lg:col-span-1">
              <div className="card-head">
                <h3 className="text-[13px] font-semibold text-ink-700">Rozkład typów opracowań</h3>
              </div>
              <div className="card-body">
                <TypePieChart data={data}/>
              </div>
            </div>
            <div className="card lg:col-span-2">
              <div className="card-head">
                <h3 className="text-[13px] font-semibold text-ink-700">
                  Liczba wykonanych opracowań w czasie (z podziałem na typy)
                </h3>
                <span className="ml-auto text-[11px] text-ink-400">granulacja: miesiąc · skumulowane słupki</span>
              </div>
              <div className="card-body">
                <MonthlyStackedBarChart data={data}/>
              </div>
            </div>
          </div>

          <div className="card">
            <div className="card-head">
              <h3 className="text-[13px] font-semibold text-ink-700">Zestawienie szczegółowe</h3>
              <span className="ml-auto text-[11px] text-ink-400">kliknij wiersz aby rozwinąć listę opracowań</span>
            </div>
            <div className="card-body !p-0">
              <table className="table-hw">
                <thead>
                  <tr>
                    <th style={{ width: 32 }}></th>
                    <th>Typ opracowania</th>
                    <th className="text-right">Liczba</th>
                    <th className="text-right">% całości</th>
                  </tr>
                </thead>
                <tbody>
                  {data.byType.length === 0 && (
                    <tr><td colSpan={4} className="text-center text-ink-400 p-4">
                      Brak wykonanych opracowań w wybranym zakresie.
                    </td></tr>
                  )}
                  {data.byType.map(b => {
                    const canExpand = b.count > 0;
                    const isOpen = expanded.has(b.type);
                    const pct = data.totalCount > 0 ? Math.round((b.count / data.totalCount) * 100) : 0;
                    return (
                      <Fragment key={b.type}>
                        <tr
                          onClick={() => canExpand && toggleExpanded(b.type)}
                          className={canExpand ? 'cursor-pointer hover:bg-ink-50' : 'text-ink-400'}
                        >
                          <td className="text-center">
                            {canExpand && (
                              <Icon name="chevron"
                                    className={'w-3 h-3 text-ink-400 transition-transform '
                                      + (isOpen ? 'rotate-90' : '')}/>
                            )}
                          </td>
                          <td>
                            <span className="inline-flex items-center gap-2">
                              <span className="inline-block w-3 h-3 rounded-sm"
                                    style={{ backgroundColor: colorForType(b.type) }}/>
                              <span className="font-semibold">{b.abbreviation}</span>
                              <span className="text-ink-500 text-[12px]">{b.displayName}</span>
                            </span>
                          </td>
                          <td className="text-right font-mono tabular-nums text-ink-800">{b.count}</td>
                          <td className="text-right font-mono tabular-nums text-ink-500 text-[12px]">
                            {b.count > 0 ? `${pct}%` : '—'}
                          </td>
                        </tr>
                        {isOpen && b.studies.map(s => (
                          <StudyDetailRow key={s.id} study={s} onClick={() => navigate(`/studies/${s.id}`)}/>
                        ))}
                      </Fragment>
                    );
                  })}
                  <tr className="bg-ink-50 font-semibold">
                    <td></td>
                    <td className="text-ink-900">Razem</td>
                    <td className="text-right font-mono tabular-nums text-ink-900">{data.totalCount}</td>
                    <td className="text-right text-ink-500">—</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

function SummaryTile({ label, value, hint }: { label: string; value: string; hint?: string }) {
  return (
    <div className="border border-ink-100 rounded-md p-4 bg-gradient-to-br from-white to-ink-50/30">
      <div className="text-[11px] uppercase tracking-wider text-ink-500 mb-1.5">{label}</div>
      <div className="text-[24px] font-bold text-ink-900 -tracking-[0.01em]">{value}</div>
      {hint && <div className="text-[11px] text-ink-400 mt-1 truncate" title={hint}>{hint}</div>}
    </div>
  );
}

function StudyDetailRow({ study, onClick }: { study: ReportStudyRef; onClick: () => void }) {
  return (
    <tr onClick={onClick}
        className="cursor-pointer bg-ink-50/40 hover:bg-water-50 border-l-[3px] border-water-200"
        title="Kliknij aby otworzyć opracowanie">
      <td></td>
      <td className="pl-2" colSpan={2}>
        <div className="flex items-baseline gap-2">
          <span className="font-mono text-[11px] text-ink-500">{study.number}</span>
          <span className="text-[12px] text-ink-800 truncate max-w-[420px]" title={study.title}>{study.title}</span>
          {study.statusName && <span className="text-[10px] text-ink-400">· {study.statusName}</span>}
        </div>
        <div className="text-[10px] text-ink-400 mt-0.5">
          {study.clientName ?? '—'}
          {study.intakeName && <> · ujęcie: {study.intakeName}</>}
        </div>
      </td>
      <td className="text-right text-[10px] text-ink-400 font-mono">
        {study.dispatchDate ? fmtDate(study.dispatchDate) : '—'}
      </td>
    </tr>
  );
}

function TypePieChart({ data }: { data: StudyReport }) {
  const slices = data.byType.filter(b => b.count > 0);
  if (slices.length === 0) {
    return <div className="text-ink-400 text-[12px] py-8 text-center">Brak opracowań w wybranym zakresie.</div>;
  }
  const chartData = {
    labels: slices.map(s => s.abbreviation),
    datasets: [{
      data: slices.map(s => s.count),
      backgroundColor: slices.map(s => colorForType(s.type)),
      borderColor: '#fff',
      borderWidth: 2,
    }],
  };
  const total = slices.reduce((a, b) => a + b.count, 0);
  const options = {
    plugins: {
      legend: {
        position: 'bottom' as const,
        labels: { font: { size: 11 }, boxWidth: 10, padding: 8 },
      },
      tooltip: {
        callbacks: {
          label: (ctx: { label: string; parsed: number; dataIndex: number }) => {
            const slice = slices[ctx.dataIndex];
            const pct = total > 0 ? Math.round((ctx.parsed / total) * 100) : 0;
            return `${slice.abbreviation} — ${slice.displayName}: ${ctx.parsed} (${pct}%)`;
          },
        },
      },
    },
    maintainAspectRatio: false,
    responsive: true,
  };
  return (
    <div style={{ height: 280 }}>
      <Pie data={chartData} options={options}/>
    </div>
  );
}

/** Wykres słupkowy stacked: każdy słupek to miesiąc, segmenty to typy opracowań. */
function MonthlyStackedBarChart({ data }: { data: StudyReport }) {
  if (data.byMonth.length === 0) {
    return <div className="text-ink-400 text-[12px] py-8 text-center">Brak danych do wyświetlenia.</div>;
  }
  // Zbiór wszystkich typów które wystąpiły w którymkolwiek miesiącu — jeden dataset per typ.
  const typesPresent = new Set<string>();
  for (const m of data.byMonth) {
    Object.keys(m.countByType).forEach(t => typesPresent.add(t));
  }
  // Kolejność z byType (sorted by count desc) — najczęstsze typy jako pierwsze (na dole stacka).
  const orderedTypes = data.byType
    .map(b => b.type)
    .filter(t => typesPresent.has(t));

  if (orderedTypes.length === 0) {
    // Wszystkie miesiące puste mimo że są w byMonth — bezpieczny fallback.
    return <div className="text-ink-400 text-[12px] py-8 text-center">Brak opracowań w zakresie.</div>;
  }

  const labels = data.byMonth.map(m => fmtMonthLabel(m.month));
  const datasets = orderedTypes.map(type => {
    const bucket = data.byType.find(b => b.type === type);
    return {
      label: bucket ? bucket.abbreviation : type,
      data: data.byMonth.map(m => m.countByType[type] ?? 0),
      backgroundColor: colorForType(type),
      borderColor: '#fff',
      borderWidth: 1,
      borderRadius: 2,
      stack: 'studies',
    };
  });

  const options = {
    plugins: {
      legend: {
        position: 'bottom' as const,
        labels: { font: { size: 11 }, boxWidth: 10, padding: 6 },
      },
      tooltip: {
        callbacks: {
          afterTitle: (ctxs: { dataIndex: number }[]) => {
            if (ctxs.length === 0) return '';
            const m = data.byMonth[ctxs[0].dataIndex];
            return m ? `Razem: ${m.count}` : '';
          },
          label: (ctx: { dataset: { label?: string }; parsed: { y: number | null } }) => {
            return `${ctx.dataset.label}: ${ctx.parsed.y ?? 0}`;
          },
        },
      },
    },
    scales: {
      x: { stacked: true, grid: { display: false }, ticks: { font: { size: 10 } } },
      y: {
        stacked: true,
        beginAtZero: true,
        ticks: { font: { size: 10 }, precision: 0, stepSize: 1 },
        grid: { color: 'rgba(0,0,0,0.05)' },
      },
    },
    maintainAspectRatio: false,
    responsive: true,
  };

  return (
    <div style={{ height: 280 }}>
      <Bar data={{ labels, datasets }} options={options}/>
    </div>
  );
}
